import { cycleInfo, cycleOn, cycleRegularity, periodDay } from "./cycle";
import { waistToHeight, whtrAimCm } from "./body";
import { DAY_MS, addDays, dayMonth, daysBetween, parseDay, weekKey, dayLabel } from "./date";
import { KCAL_PER_KG_FAT } from "./deficit";
import { dayKcal } from "./energy";
import { basedOn, type Insight, type Window } from "./insight";
import { foodOn } from "./intake";
import { averagable, dayIn, dayLedger, weightOn, type DayLedger } from "./ledger";
import { beforePlan, neutralDay } from "./plan";
import { sessionPct } from "./queue";
import { dayFull } from "./stats";
import {
  ETA_MIN_SPAN_DAYS,
  ETA_MIN_WEIGH_INS,
  WAIST_ALPHA,
  WEIGHT_ALPHA,
  etaRange,
  etaToTarget,
  lostSoFar,
  paceVerdict,
  type EtaRange,
  type LostSoFar,
  type PaceVerdict,
  ewma,
  projectTrend,
  tooFast,
  trendRate,
  waistSeries,
  weightSeries,
  type Pt,
} from "./trend";
import type { CycleRegularity, KayaState } from "./types";
import { weekDone, weekRows } from "./week";

/**
 * What the Progress pages show, worked out once and away from the markup (invariant 4): the two
 * trends, the ledger over a fortnight, the four-week calendar in words, and the cycle tallies.
 * Every function takes the day it is answering for, so none of them reads the clock.
 */

/** A real minus and a signed plus, so a loss and a gain line up in tabular figures. */
export function signed(n: number, digits = 0): string {
  const s = Math.abs(n).toLocaleString("en-GB", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (Number(s.replace(/,/g, "")) === 0) return s;
  return (n > 0 ? "+" : n < 0 ? "−" : "") + s;
}

/** A steady daily balance, as the kilograms a week it would move if it held. */
function kgPerWeekFromKcal(kcalPerDay: number): number {
  return (kcalPerDay * 7) / KCAL_PER_KG_FAT;
}

/* ---------- trends ---------- */

export type Metric = "weight" | "waist";

/** Below these a weekly change is scale or tape noise, and calling it a direction would overclaim. */
const HOLDING: Record<Metric, number> = { weight: 0.05, waist: 0.1 };
/** Waist is taped about weekly, so its rate needs a four-week window to find three readings. */
const RATE_WINDOW: Record<Metric, number> = { weight: 21, waist: 28 };
/** The chart shows the last ~90 days; older readings squeeze the part that is still changing. */
const CHART_DAYS = 90;
/** How far the dashed line and the "if this pace holds" figure look ahead. */
const LOOKAHEAD_DAYS = 28;

export interface TrendView {
  metric: Metric;
  unit: "kg" | "cm";
  raw: Pt[];
  trend: Pt[];
  /** The latest READING — the number the user logged — or null with none (invariant 11). The
   *  smoothed trend lags a daily weigher by about nine days (alpha 0.1), so leading with it read
   *  as "the previous weight" after every new weigh-in. */
  now: number | null;
  /** When `now` was logged. */
  nowDate: string | null;
  /** The smoothed trend at its last point: the rate's and the chart's basis, shown as a qualifier. */
  trendNow: number | null;
  /** Change per week, or null until there are enough readings for one. */
  rate: number | null;
  direction: "holding" | "down" | "up" | null;
  /** The goal's own target — drawn as the chart's rule — or null for the other metric. */
  target: number | null;
  /** How far above its target the trend still is, when a target is set and not yet reached. */
  toGo: number | null;
  targetValue: number | null;
  eta: { days: number; date: string } | null;
  /** Where the trend would be four weeks out at this rate. */
  ahead: Pt | null;
  /** The dashed line: from the last trend point to the target date (when near) or four weeks. */
  projection: Pt[] | null;
  tooFast: boolean;
  /** Change since the first reading, for "3.8 cm since 1 Jul". */
  since: { date: string; change: number } | null;
}

/**
 * `ignore` names readings the trend should step over — the cycle-affected weigh-ins (ADR-033 §2).
 * They stay in `raw`, and the latest one can still be the hero figure: it is what she logged.
 */
export function trendView(
  S: KayaState,
  metric: Metric,
  today: string,
  ignore?: (ds: string) => boolean
): TrendView {
  const raw = metric === "weight" ? weightSeries(S) : waistSeries(S);
  const kept = ignore ? raw.filter((p) => !ignore(p.date)) : raw;
  const basis = kept.length ? kept : raw;
  const trend = metric === "weight" ? ewma(basis) : ewma(basis, WAIST_ALPHA);
  const last: Pt | undefined = trend[trend.length - 1];
  const latest: Pt | undefined = raw[raw.length - 1];
  const now = latest ? latest.v : null;
  const trendNow = last ? last.v : null;
  const rate = trendRate(trend, RATE_WINDOW[metric], today);
  const direction =
    rate === null ? null : Math.abs(rate) < HOLDING[metric] ? "holding" : rate < 0 ? "down" : "up";

  const targetValue = (metric === "weight" ? S.profile.targetWeight : S.profile.targetWaist) || null;
  const target = S.profile.goal === metric ? targetValue : null;
  const toGo = targetValue && now !== null && now > targetValue ? now - targetValue : null;

  // Counted from the latest reading, which is the figure on screen, so "4.4 kg to go" and the
  // date agree; counting from today would shift the date by however old the weigh-in is. The
  // rate itself still comes from the trend, which a single odd reading cannot swing.
  const eta = metric === "weight" && latest ? etaToTarget(S, latest.v, rate, latest.date) : null;
  const ahead = latest && rate !== null ? projectTrend(latest, rate, addDays(today, LOOKAHEAD_DAYS)) : null;
  const lineEnd =
    eta && daysBetween(today, eta.date) <= CHART_DAYS ? eta.date : addDays(today, LOOKAHEAD_DAYS);
  const projection = last && rate !== null ? [last, projectTrend(last, rate, lineEnd)] : null;

  const first = raw[0];
  const since = first && latest && raw.length > 1 ? { date: first.date, change: latest.v - first.v } : null;

  return {
    metric,
    unit: metric === "weight" ? "kg" : "cm",
    raw,
    trend,
    now,
    nowDate: latest ? latest.date : null,
    trendNow,
    rate,
    direction,
    target,
    toGo,
    targetValue,
    eta,
    ahead,
    projection,
    tooFast: metric === "weight" ? tooFast(S, trend) : false,
    since,
  };
}

const WORDS: Record<Metric, { down: string; up: string; none: string; few: string; one: string }> = {
  weight: {
    down: "losing",
    up: "gaining",
    none: "No weigh-ins yet",
    few: "Not enough weigh-ins yet for a rate; it needs about ten days of them",
    one: "one weigh-in so far",
  },
  waist: {
    down: "shrinking",
    up: "growing",
    none: "No waist measurements yet",
    few: "Not enough measurements yet for a rate; it needs three or so over a couple of weeks",
    one: "one measurement so far",
  },
};

/** The unit after the hero figure, or how thin the evidence still is. */
export function trendUnit(tv: TrendView): string {
  if (tv.raw.length === 1) return `${tv.unit}, ${WORDS[tv.metric].one}`;
  return tv.unit;
}

/** "Weight · today" / "Waist · Wed 23 Sep": the figure is the reading, dated. */
export function trendLabel(tv: TrendView, today: string): string {
  const name = tv.metric === "weight" ? "Weight" : "Waist";
  if (!tv.nowDate) return name;
  return `${name} · ${tv.nowDate === today ? "today" : dayLabel(tv.nowDate)}`;
}

/** "trend 77.1 kg · −0.35 kg/week · losing · 4.4 kg to 72", or why there is no rate yet. The
 *  trend is named only when it differs from the reading by a visible amount. */
export function trendLine(tv: TrendView): string {
  const w = WORDS[tv.metric];
  if (!tv.raw.length) return w.none;
  const toGo = tv.toGo !== null && tv.targetValue ? ` · ${tv.toGo.toFixed(1)} ${tv.unit} to ${tv.targetValue}` : "";
  const trendPart =
    tv.trendNow !== null && tv.now !== null && tv.raw.length > 1 && Math.abs(tv.trendNow - tv.now) >= 0.05
      ? `trend ${tv.trendNow.toFixed(1)} ${tv.unit} · `
      : "";
  if (tv.rate === null || tv.direction === null) return trendPart + w.few + toGo;
  const word = tv.direction === "holding" ? "holding" : tv.direction === "down" ? w.down : w.up;
  return `${trendPart}${signed(tv.rate, tv.metric === "weight" ? 2 : 1)} ${tv.unit}/week · ${word}${toGo}`;
}

/** The forecast, always labelled as one — or null when there is nothing honest to say. */
export function trendForecast(tv: TrendView): string | null {
  if (!tv.ahead) return null;
  const four = `~${tv.ahead.v.toFixed(1)} ${tv.unit} by ${dayMonth(tv.ahead.date)}`;
  const eta = tv.eta && tv.targetValue ? `, ${tv.targetValue} ${tv.unit} around ${dayMonth(tv.eta.date)}` : "";
  return `If this pace holds: ${four}${eta}. An estimate, not a promise.`;
}

/** The last ~90 days for the chart, unless that would leave fewer than two points. The trend
 *  itself is always computed on everything. */
export function chartWindow(ps: Pt[], today: string): [string, number][] {
  const from = addDays(today, -CHART_DAYS);
  const kept = ps.filter((p) => p.date >= from);
  return (kept.length >= 2 ? kept : ps).map((p) => [p.date, p.v]);
}

/* ---------- energy over time ---------- */

export interface LedgerSpan {
  /** Finished days, oldest first. Today is left out: its food log is still being written, and
   *  counting it would book a deficit that has not happened (as `projectFromLedger`). */
  days: DayLedger[];
  /** Days with both In and Out known — the only days an average may use. */
  paired: number;
  avgIn: number | null;
  avgOut: number | null;
  avgNet: number | null;
  kgPerWeek: number | null;
}

export function ledgerSpan(S: KayaState, today: string, now: Date, n = 14): LedgerSpan {
  const days: DayLedger[] = [];
  for (let i = n; i >= 1; i--) days.push(dayLedger(S, addDays(today, -i), now));
  const both = days.filter(averagable);
  const k = both.length;
  const sumIn = both.reduce((a, d) => a + (d.in ?? 0), 0);
  const sumOut = both.reduce((a, d) => a + (d.out ?? 0), 0);
  const avgNet = k ? Math.round((sumIn - sumOut) / k) : null;
  return {
    days,
    paired: k,
    avgIn: k ? Math.round(sumIn / k) : null,
    avgOut: k ? Math.round(sumOut / k) : null,
    avgNet,
    kgPerWeek: avgNet === null ? null : kgPerWeekFromKcal(avgNet),
  };
}

export interface WorkSpan {
  /** Oldest first, today last. */
  days: string[];
  vals: number[];
  total: number;
  /** Days with any Work at all. */
  activeDays: number;
  /** Mean over the days with Work, or null with none — a fortnight off is not "0 a day". */
  avg: number | null;
}

/** Work kcal (sessions, treadmill, walks) by day — what the page shows with food off. */
export function workSpan(S: KayaState, today: string, n = 14): WorkSpan {
  const days: string[] = [];
  for (let i = n - 1; i >= 0; i--) days.push(addDays(today, -i));
  const vals = days.map((ds) => dayKcal(S, ds));
  const on = vals.filter((v) => v > 0);
  const total = vals.reduce((a, b) => a + b, 0);
  return {
    days,
    vals,
    total,
    activeDays: on.length,
    avg: on.length ? Math.round(on.reduce((a, b) => a + b, 0) / on.length) : null,
  };
}

/** What the gap between the food log and the scale most likely means. */
export function ledgerVerdict(bias: number | null): string {
  if (bias === null)
    return "Once the trend has two weeks of weigh-ins behind it, this can also say which side of the ledger is off.";
  if (bias < 0.9)
    return `The scale is moving slower than the log predicts: either the Out estimate runs about ${Math.round(
      (1 - bias) * 100
    )}% high, or some food is not getting logged. Cooking oil and snacks are the usual gaps.`;
  if (bias > 1.1)
    return "The scale is moving faster than the log predicts: the Out estimate may be low, or early water loss is flattering the trend.";
  return "The log and the scale agree within 10%, so the budget can be trusted as it is.";
}

/* ---------- consistency ---------- */

export type CalState =
  | "both"
  | "done"
  | "rescued"
  | "freeze"
  | "rested"
  | "part"
  | "walk"
  | "extra"
  | "rest"
  | "missed"
  | "today"
  | "future"
  | "before";

/** Every state as a word — the calendar has no colour key (audit §6). */
export const CAL_WORD: Record<CalState, string> = {
  both: "both",
  done: "done",
  rescued: "rescued",
  freeze: "freeze",
  rested: "rested",
  part: "part",
  walk: "walk",
  extra: "extra",
  rest: "rest",
  missed: "missed",
  today: "today",
  future: "–",
  before: "–",
};

/** What the state means in a sentence, for the cell's accessible name. */
export const CAL_SENTENCE: Record<CalState, string> = {
  both: "session and treadmill done",
  done: "session done",
  rescued: "rescued",
  freeze: "covered by a freeze",
  rested: "period rest, streak kept",
  part: "session part done",
  walk: "treadmill only",
  extra: "extra moves only",
  rest: "rest day",
  missed: "missed",
  today: "today, not started",
  future: "not yet",
  before: "before the plan started",
};

export interface CalCell {
  ds: string;
  state: CalState;
  /** "period" on a logged or current period day, "predicted" on an expected one. */
  period: "period" | "predicted" | null;
}

export interface CalendarView {
  cells: CalCell[];
  /** Past days the plan scheduled a session on (today, rest days and days before the start
   *  excluded). */
  scheduled: number;
  /** …of which something was done, or the day was rescued or frozen. */
  covered: number;
}

/**
 * Four Monday-first weeks ending with the current one. The cells after today are "not yet",
 * so the grid lines up under its weekday heading instead of starting on whatever day it was
 * 27 days ago.
 */
export function calendarView(S: KayaState, today: string, weeks = 4): CalendarView {
  const monday = weekKey(parseDay(today));
  const start = addDays(monday, -7 * (weeks - 1));
  const cells: CalCell[] = [];
  let scheduled = 0;
  let covered = 0;

  for (let i = 0; i < weeks * 7; i++) {
    const ds = addDays(start, i);
    const c = cycleOn(S) ? cycleInfo(S, ds) : null;
    const period = c && c.inPeriod ? "period" : c && c.predicted ? "predicted" : null;
    const state = dayState(S, ds, today);
    cells.push({ ds, state, period });
    if (ds < today && !neutralDay(S, ds) && !beforePlan(S, ds)) {
      scheduled++;
      if (state !== "missed") covered++;
    }
  }
  return { cells, scheduled, covered };
}

function dayState(S: KayaState, ds: string, today: string): CalState {
  if (ds > today) return "future";
  const L = S.logs[ds];
  const full = dayFull(S, ds);
  if (full && L?.treadmill) return "both";
  if (full) return "done";
  if (L?.rescued) return "rescued";
  if (L?.frozen) return "freeze";
  if (L?.periodSkip) return "rested";
  if ((sessionPct(S, ds) ?? 0) > 0) return "part";
  if (L?.treadmill) return "walk";
  if (L?.extras && L.extras.length) return "extra";
  if (beforePlan(S, ds)) return "before";
  if (neutralDay(S, ds)) return "rest";
  return ds === today ? "today" : "missed";
}

/** Runs of consecutive period days as "12–16 Sep", in words, for the line under the calendar. */
export function periodRuns(cells: CalCell[], kind: "period" | "predicted"): string[] {
  const out: string[] = [];
  let run: string[] = [];
  const flush = () => {
    if (!run.length) return;
    const a = run[0];
    const b = run[run.length - 1];
    const sameMonth = parseDay(a).getMonth() === parseDay(b).getMonth();
    out.push(
      a === b
        ? dayMonth(a)
        : sameMonth
          ? `${parseDay(a).getDate()}–${dayMonth(b)}`
          : `${dayMonth(a)} – ${dayMonth(b)}`
    );
    run = [];
  };
  for (const c of cells) {
    if (c.period === kind) run.push(c.ds);
    else flush();
  }
  flush();
  return out;
}

/** "3 h 10" / "35 min" — active time at a glance. */
export function hoursMinutes(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
}

/* ---------- cycle ---------- */

export interface CycleStats {
  /** Logged starts, oldest first. */
  starts: string[];
  /** Mean gap between logged starts; the set cycle length until there are two. */
  avgLen: number;
  /** True when `avgLen` comes from logged starts rather than the setting. */
  avgFromLog: boolean;
  periodLen: number;
  gentle: number;
  rested: number;
  cramps: number;
  care: number;
  reg: CycleRegularity;
}

export function cycleStats(S: KayaState, now: number): CycleStats {
  const starts = (S.cycle.starts || []).slice().sort();
  let avgLen = S.cycle.length || 28;
  if (starts.length >= 2) {
    const gaps: number[] = [];
    for (let i = 1; i < starts.length; i++)
      gaps.push((parseDay(starts[i]).getTime() - parseDay(starts[i - 1]).getTime()) / DAY_MS);
    avgLen = Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length);
  }
  const pdays = Object.keys(S.logs).filter((ds) => periodDay(S, ds));
  return {
    starts,
    avgLen,
    avgFromLog: starts.length >= 2,
    periodLen: S.cycle.periodLen || 5,
    gentle: pdays.filter((ds) => S.logs[ds].sessionKey === "P" && dayFull(S, ds)).length,
    rested: pdays.filter((ds) => S.logs[ds].periodSkip).length,
    cramps: pdays.filter((ds) => (S.logs[ds].symptoms || []).includes("cramps")).length,
    care: Object.values(S.logs).filter((L) => (L.extras || []).includes("Y")).length,
    reg: cycleRegularity(S, now),
  };
}

/** Each logged start with what to say under it: the current cycle's day, or how long that cycle
 *  ran until the next start. Newest first. */
export function loggedPeriods(
  S: KayaState,
  today: string
): { ds: string; current: boolean; day: number | null; length: number | null; ended: string | null }[] {
  const starts = (S.cycle.starts || []).slice().sort();
  return starts
    .map((ds, i) => {
      const next = starts[i + 1];
      const current = !next && ds <= today;
      return {
        ds,
        current,
        day: current ? daysBetween(ds, today) + 1 : null,
        length: next ? daysBetween(ds, next) : null,
        ended: S.cycle.ends?.[ds] ?? null,
      };
    })
    .reverse();
}

/**
 * "5/6 this week" — the same count Train shows as "5 of 6 done", from the same rows: a rescued day
 * counts, a freeze does not. Two screens once disagreed by the rescued day (ADR-029 §2).
 */
export function weekFigure(S: KayaState, today: string): string {
  const wk = weekDone(weekRows(S, today));
  return `${wk.done}/${wk.of} this week`;
}
/** Badges that only mean anything with cycle tracking on; left out of counts while it is off. */
export const CYCLE_BADGES = ["cycle1", "gentle3", "care5"];

/* ---------- the profile: hero, body and energy (ADR-033) ---------- */

/** Whether a day sits in the days around a period (5 before through day 3). Supplied by the
 *  cycle module; with tracking off it never fires. */
export type CycleAffected = (ds: string) => boolean;

const never: CycleAffected = () => false;

/** Minimum data per insight, from the research §2 "Min" column. */
const RHYTHM_MIN_WEIGH_INS = 21;
const WEEKDAY_MIN_READINGS = 2;
/** A weekday bias under this is inside a single reading's noise. */
const WEEKDAY_MIN_BIAS = 0.2;
const WAIST_MIN_READINGS = 2;
/** Without this much loss, waist-per-kg divides noise by noise. */
const COMPOSITION_MIN_KG = 0.5;
/** ~1 cm of waist per kg is typical when the loss is fat; 0.8 leaves room for tape error. */
const FAT_CM_PER_KG = 0.8;
const ENERGY_MIN_LOGGED = 7;
const ENERGY_MIN_TREND_SPAN = 14;
const IMPLIED_MIN_LOGGED = 14;
const IMPLIED_MIN_TREND_SPAN = 21;
const IMPLIED_MIN_COVERAGE = 0.6;
/** Past this share of cycle-affected weigh-ins the scale's slope is mostly water (research E2). */
const IMPLIED_MAX_AFFECTED = 0.4;
/** The log and the scale "agree" within this many kilograms over the window. */
const AGREE_KG = 0.3;
/** |implied − estimate| under this is no gap worth naming. */
const IMPLIED_SAME_KCAL = 100;
const STALL_DAYS = 21;
const STALL_KG = 0.15;
const STALL_MIN_WEIGH_INS = 10;
const STALL_IN_UP_KCAL = 100;
const STALL_WORK_DOWN_KCAL = 50;

const WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function mean(xs: number[]): number | null {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}

function plural(n: number, one: string, many = one + "s"): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "8" for a whole number, "7.5" otherwise — "3.4 of 8 kg". */
function kgWord(x: number): string {
  const r = Math.round(x * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/** An estimate in kcal, to the nearest 10: "2,150". */
function kcalWord(x: number): string {
  return (Math.round(Math.abs(x) / 10) * 10).toLocaleString("en-GB");
}

function moreDays(n: number): string {
  return `after ${plural(Math.max(1, n), "more day", "more days")}`;
}

/** Readings up to `today`, with the trend over the ones the cycle does not affect. */
function cleanWeights(S: KayaState, today: string, affected: CycleAffected) {
  const raw = weightSeries(S).filter((p) => p.date <= today);
  const kept = raw.filter((p) => !affected(p.date));
  return { raw, trend: ewma(kept.length ? kept : raw) };
}

interface Residual {
  date: string;
  v: number;
  /** The reading less the trend as it stood before it; null for the first. */
  r: number | null;
  affected: boolean;
}

/**
 * Each reading against the trend as it stood just before that reading (a one-step residual), the
 * trend fed only unaffected readings. Measured the same way for every reading so an affected one
 * and an ordinary one can be compared; while losing, every residual runs a little low (the trend
 * lags), which is why the figures below are relative to the ordinary readings' mean.
 */
function residuals(raw: Pt[], affected: CycleAffected): Residual[] {
  const out: Residual[] = [];
  let state: Pt | null = null;
  for (const p of raw) {
    const a = affected(p.date);
    out.push({ date: p.date, v: p.v, r: state ? p.v - state.v : null, affected: a });
    if (a) continue;
    if (!state) {
      state = { date: p.date, v: p.v };
    } else {
      const k: number = 1 - Math.pow(1 - WEIGHT_ALPHA, Math.max(0, daysBetween(state.date, p.date)));
      state = { date: p.date, v: state.v + k * (p.v - state.v) };
    }
  }
  return out;
}

/** The trend as it stood on `ds`: its last point on or before that day. */
function trendAt(trend: Pt[], ds: string): number | null {
  let v: number | null = null;
  for (const p of trend) {
    if (p.date > ds) break;
    v = p.v;
  }
  return v;
}

/* --- body --- */

export interface WeighInRhythm {
  /** Weigh-ins up to today. */
  n: number;
  /** Mean day-to-day change between consecutive ordinary readings, or null under 21 weigh-ins. */
  swing: number | null;
  /** The weekday that runs highest against her other readings, when it does by 0.2 kg or more. */
  high: { dow: number; name: string; bias: number; n: number } | null;
}

/** How noisy her scale is, and whether one weekday runs high (research B6). Cycle-affected
 *  readings are left out, so a period cannot pass for a Monday effect. */
export function weighInRhythm(
  S: KayaState,
  today: string,
  cycleAffected: CycleAffected = never
): WeighInRhythm {
  const raw = weightSeries(S).filter((p) => p.date <= today);
  const n = raw.length;
  if (n < RHYTHM_MIN_WEIGH_INS) return { n, swing: null, high: null };
  const clean = raw.filter((p) => !cycleAffected(p.date));
  const swing = mean(clean.slice(1).map((p, i) => Math.abs(p.v - clean[i].v)));
  const res = residuals(raw, cycleAffected).filter((x) => !x.affected && x.r !== null);
  const all = mean(res.map((x) => x.r ?? 0)) ?? 0;
  let high: WeighInRhythm["high"] = null;
  for (let dow = 0; dow < 7; dow++) {
    const on = res.filter((x) => parseDay(x.date).getDay() === dow);
    if (on.length < WEEKDAY_MIN_READINGS) continue;
    const bias = (mean(on.map((x) => x.r ?? 0)) ?? 0) - all;
    if (bias >= WEEKDAY_MIN_BIAS && (!high || bias > high.bias))
      high = { dow, name: WEEKDAY[dow], bias, n: on.length };
  }
  return { n, swing, high };
}

export interface TaggedReadings {
  /** The affected readings, oldest first, with how far each sat above the trend. */
  readings: { date: string; v: number; r: number | null }[];
  count: number;
  /** Every weigh-in up to today. */
  of: number;
  /** How much higher the affected readings ran than her ordinary ones, or null. */
  high: number | null;
}

/** The weigh-ins that fell in the days around a period, and what they did (research C3). */
export function readingsTagged(
  S: KayaState,
  cycleAffected: CycleAffected,
  today = "9999-12-31"
): TaggedReadings {
  const raw = weightSeries(S).filter((p) => p.date <= today);
  const res = residuals(raw, cycleAffected);
  const hit = res.filter((x) => x.affected);
  const a = mean(hit.filter((x) => x.r !== null).map((x) => x.r ?? 0));
  const b = mean(res.filter((x) => !x.affected && x.r !== null).map((x) => x.r ?? 0));
  return {
    readings: hit.map(({ date, v, r }) => ({ date, v, r })),
    count: hit.length,
    of: raw.length,
    high: a === null || b === null ? null : a - b,
  };
}

export interface LossComposition {
  from: string;
  to: string;
  waists: number;
  /** Waist lost, first to latest tape; negative is a gain. */
  waistCm: number;
  /** Weight lost over the same dates. */
  weightKg: number;
  cmPerKg: number;
  /** True when the waist fell in step: "mostly fat, not muscle". */
  fat: boolean;
}

/**
 * What the loss is made of, as far as a tape can tell (research B5): the waist falling about a
 * centimetre per kilogram is what fat leaving looks like; the scale falling with the waist still
 * is what water and muscle look like. Null without two waists or half a kilo lost between them.
 */
export function lossComposition(S: KayaState, today: string): LossComposition | null {
  const w = waistSeries(S).filter((p) => p.date <= today);
  if (w.length < WAIST_MIN_READINGS) return null;
  const first = w[0];
  const last = w[w.length - 1];
  const weightKg = weightOn(S, first.date) - weightOn(S, last.date);
  if (weightKg < COMPOSITION_MIN_KG) return null;
  const waistCm = first.v - last.v;
  const cmPerKg = waistCm / weightKg;
  return {
    from: first.date,
    to: last.date,
    waists: w.length,
    waistCm,
    weightKg,
    cmPerKg,
    fat: cmPerKg >= FAT_CM_PER_KG,
  };
}

/* --- the hero --- */

export interface HeroView {
  /** The latest reading — the body figure (invariant 9) — or null with none. */
  reading: number | null;
  readingDate: string | null;
  /** The trend beside it, ignoring cycle-affected readings. */
  trend: number | null;
  rate: number | null;
  target: number | null;
  /** From the reading, not the trend (ADR-029 §9). */
  toGo: number | null;
  eta: EtaRange | null;
  pace: PaceVerdict | null;
  lost: LostSoFar | null;
  weighIns: number;
  /** Days from the first weigh-in through today, counting both. */
  daysSoFar: number;
  /** The latest reading fell in the days around a period. */
  readingAffected: boolean;
  /** Today itself does. */
  todayAffected: boolean;
}

export function heroView(S: KayaState, today: string, cycleAffected: CycleAffected = never): HeroView {
  const tv = trendView(S, "weight", today, cycleAffected);
  const raw = tv.raw.filter((p) => p.date <= today);
  const latest: Pt | undefined = raw[raw.length - 1];
  const target = S.profile.goal === "weight" && S.profile.targetWeight ? S.profile.targetWeight : null;
  const span = raw.length ? daysBetween(raw[0].date, raw[raw.length - 1].date) : 0;
  const eta = target && latest ? etaRange(tv.trend, target, today, tv.rate, latest) : null;
  return {
    reading: latest ? latest.v : null,
    readingDate: latest ? latest.date : null,
    trend: tv.trendNow,
    rate: tv.rate,
    target,
    toGo: target && latest && latest.v > target ? latest.v - target : null,
    eta,
    pace: span >= ETA_MIN_SPAN_DAYS ? paceVerdict(S, tv.rate, tv.trendNow) : null,
    lost: lostSoFar(S),
    weighIns: raw.length,
    daysSoFar: raw.length ? daysBetween(raw[0].date, today) + 1 : 0,
    readingAffected: latest ? cycleAffected(latest.date) : false,
    todayAffected: cycleAffected(today),
  };
}

function heroBasis(h: HeroView): string {
  return basedOn(
    plural(h.weighIns, "weigh-in"),
    h.daysSoFar < ETA_MIN_SPAN_DAYS
      ? plural(h.daysSoFar, "day")
      : plural(Math.round((h.daysSoFar - 1) / 7), "week")
  );
}

/** The hero's sentences: the goal date (or what unlocks it), the pace, and the cycle caveat. */
export function heroInsights(S: KayaState, today: string, cycleAffected: CycleAffected = never): Insight[] {
  const h = heroView(S, today, cycleAffected);
  const out: Insight[] = [];
  const based = heroBasis(h);

  if (h.target !== null) {
    const wait = (text: string, w: string, b = based): Insight => ({ key: "hero.eta", text, basedOn: b, wait: w });
    const span =
      h.weighIns > 1 && h.readingDate ? daysBetween(weightSeries(S)[0].date, h.readingDate) : 0;
    if (!h.weighIns) {
      out.push(wait("Trend and goal date appear after 14 days of weigh-ins.", "after the first weigh-in", "no weigh-ins yet"));
    } else if (h.daysSoFar < ETA_MIN_SPAN_DAYS) {
      out.push(wait("Trend and goal date appear after 14 days of weigh-ins.", moreDays(ETA_MIN_SPAN_DAYS - h.daysSoFar)));
    } else if (h.weighIns < ETA_MIN_WEIGH_INS) {
      const n = ETA_MIN_WEIGH_INS - h.weighIns;
      out.push(wait("The goal date appears after 8 weigh-ins.", `after ${plural(n, "more weigh-in")}`));
    } else if (span < ETA_MIN_SPAN_DAYS) {
      out.push(wait("The goal date appears once the weigh-ins span 14 days.", "after the next weigh-in"));
    } else if (h.eta) {
      const e = h.eta;
      const range = e.capped
        ? `likely from ${dayMonth(e.from)}, possibly more than a year out`
        : `likely between ${dayMonth(e.from)} and ${dayMonth(e.to)}`;
      out.push({
        key: "hero.eta",
        text: `At this pace you reach ${kgWord(h.target)} kg around ${dayLabel(e.likely)} — ${range}.`,
        basedOn: based,
        tag: "est",
      });
    } else if (h.toGo !== null) {
      out.push({
        key: "hero.eta",
        text:
          h.rate !== null && h.rate < -0.02
            ? `No goal date: at this pace ${kgWord(h.target)} kg is more than a year away.`
            : "No goal date while the trend is not falling.",
        basedOn: based,
      });
    }
  }

  if (h.pace) {
    const p = h.pace;
    const l = h.lost;
    const gone = l && l.kg > 0 && l.of !== null ? `${l.kg.toFixed(1)} of ${kgWord(l.of)} kg gone in ${plural(l.weeks, "week")}` : "";
    const lost = gone ? `, and ${gone}` : "";
    // The band's own dash becomes a comma so the lost-so-far clause can take the closing dash.
    const words =
      p.band === "fast" && p.early ? "fast, as the first weeks usually are: most of it is water" : p.words.replace(" — ", ", ");
    const text =
      p.band === "flat"
        ? `Holding steady — under 0.1 % of your weight a week either way${lost}.`
        : p.band === "gaining"
          ? `Gaining ${(-p.pct).toFixed(2)} % of your weight a week${lost}.`
          : `Losing ${p.pct.toFixed(2)} % of your weight a week — ${words}${gone ? ` — and ${gone}` : ""}.`;
    out.push({ key: "hero.pace", text, basedOn: based });
  }

  if (cycleOn(S) && h.readingAffected && h.readingDate) {
    out.push({
      key: "hero.cycle",
      text: `${
        h.readingDate === today ? "Today's reading" : `The ${dayLabel(h.readingDate)} reading`
      } fell in the days around a period; the trend ignores it.`,
      basedOn: "the 5 days before a period through its day 3",
      tag: "cycle",
    });
  }
  return out;
}

/** Body's sentences: what the loss is made of, waist to height, the scale's rhythm, the cycle. */
export function bodyInsights(S: KayaState, today: string, cycleAffected: CycleAffected = never): Insight[] {
  const out: Insight[] = [];
  const weights = weightSeries(S).filter((p) => p.date <= today);
  const waists = waistSeries(S).filter((p) => p.date <= today);
  const comp = lossComposition(S, today);

  if (waists.length < WAIST_MIN_READINGS) {
    out.push({
      key: "body.waist",
      text: "Log a waist to see where the loss comes from.",
      basedOn: waists.length ? "1 waist" : "no waist yet",
      wait: waists.length ? "after 1 more waist" : "after 1 waist",
    });
  } else if (comp) {
    const cm = comp.waistCm.toFixed(1);
    const kg = comp.weightKg.toFixed(1);
    out.push({
      key: "body.composition",
      text: comp.fat
        ? `Your waist fell in step with your weight, ${cm} cm for ${kg} kg: the loss is mostly fat, not muscle.`
        : comp.waistCm > 0
          ? `Your weight fell ${kg} kg and your waist ${cm} cm: the scale moved more than the waist — hold protein and strength.`
          : `Your weight fell ${kg} kg but your waist did not: the scale moved more than the waist — hold protein and strength.`,
      basedOn: basedOn(
        `${plural(weights.length, "weigh-in")} and ${plural(comp.waists, "waist")}`,
        plural(Math.round(daysBetween(comp.from, comp.to) / 7), "week")
      ),
      link: { to: "/progress/energy", label: "What is driving it" },
    });
  }

  const lastWaist = waists[waists.length - 1];
  const ratio = lastWaist ? waistToHeight(S, lastWaist.v) : null;
  const aim = whtrAimCm(S);
  if (lastWaist && ratio !== null && aim !== null) {
    let text: string;
    if (ratio >= 0.5) {
      let when = "";
      const target = S.profile.goal === "weight" ? S.profile.targetWeight : 0;
      if (comp && comp.fat && target) {
        const at = weightOn(S, lastWaist.date) - (lastWaist.v - aim) / comp.cmPerKg;
        when =
          at > target + 0.5
            ? `, likely before ${kgWord(target)} kg`
            : at >= target - 0.5
              ? `, likely around ${kgWord(target)} kg`
              : `, likely after ${kgWord(target)} kg — near ${Math.round(at)} kg`;
      }
      text = `Waist to height is ${ratio.toFixed(2)}. Under ${Math.floor(aim)} cm it drops below 0.50${when}.`;
    } else {
      text = `Waist to height is ${ratio.toFixed(2)} — under 0.50, the line where risk starts rising.`;
    }
    out.push({
      key: "body.whtr",
      text,
      basedOn: basedOn(`the ${dayMonth(lastWaist.date)} waist`, `${S.profile.heightCm} cm`),
    });
  }

  const rh = weighInRhythm(S, today, cycleAffected);
  if (rh.swing === null) {
    const n = RHYTHM_MIN_WEIGH_INS - rh.n;
    out.push({
      key: "body.rhythm",
      text: "Your day-to-day swing appears after 21 weigh-ins.",
      basedOn: rh.n ? plural(rh.n, "weigh-in") : "no weigh-ins yet",
      wait: `after ${plural(n, "more weigh-in")}`,
    });
  } else {
    const hi = rh.high;
    const clause = hi ? ` and ${hi.name}s run ${hi.bias.toFixed(1)} kg high` : "";
    const advice = !hi
      ? "Any single reading can be that far off the trend."
      : [0, 1, 6].includes(hi.dow)
        ? "Weigh mid-week."
        : `Compare ${hi.name}s with ${hi.name}s.`;
    out.push({
      key: "body.rhythm",
      text: `Readings swing ±${rh.swing.toFixed(1)} kg a day${clause}. ${advice}`,
      basedOn: basedOn(
        plural(rh.n, "weigh-in"),
        plural(Math.round(daysBetween(weights[0].date, weights[weights.length - 1].date) / 7), "week")
      ),
    });
  }

  if (cycleOn(S)) {
    const tg = readingsTagged(S, cycleAffected, today);
    if (tg.count && tg.high !== null) {
      const ran = tg.high >= 0.05 ? `ran ${tg.high.toFixed(1)} kg high` : "ran no higher than the rest";
      out.push({
        key: "body.cycle",
        text: `${plural(tg.count, "reading")} in the days around a period ${ran}. They are tagged, not counted.`,
        basedOn: `${tg.count} of ${plural(tg.of, "weigh-in")}`,
        tag: "cycle",
        link: { to: "/progress/cycle", label: "See your cycle" },
      });
    }
  }
  return out;
}

/* --- energy --- */

export interface StallCause {
  kind: "cycle" | "coverage" | "eating" | "moving";
  text: string;
}

export interface EnergyView {
  window: Window;
  /** Finished days in the window, oldest first — today is left out, as `ledgerSpan`. The window
   *  never reaches back before the plan's start. */
  days: DayLedger[];
  /** Days in the window, and days with food logged. */
  of: number;
  logged: number;
  /** Days with both In and Out — the only days an In or a deficit average may use. */
  paired: number;
  /** In on logged days. */
  inAvg: number | null;
  /** Out over every day in the window (est.). */
  outAvg: number | null;
  /** Work over every day in the window, rest days included; null with none at all. */
  workAvg: number | null;
  /** In less Out on logged days; negative is a deficit. */
  deficit: number | null;
  /** What the deficit should have done over the window, and what the trend did. */
  expectedKg: number | null;
  actualKg: number | null;
  /** |expected − actual|, when both are known and the log is thick enough to compare. */
  gapKg: number | null;
  /** The burn that would make the log and the scale agree. */
  impliedOut: number | null;
  /** Why `impliedOut` is null. */
  impliedBlock: "logged" | "trend" | "coverage" | "cycle" | null;
  /** Deficit by source on logged days: eating under the resting burn, and Work. Sums to −deficit. */
  split: { food: number; work: number } | null;
  weighIns: number;
  affectedWeighIns: number;
  /** Span of the trend inside the window, in days. */
  trendSpan: number;
  /** The trend flat within ±0.15 kg over 21 days; null until it can be told. */
  stalled: boolean | null;
  change21: number | null;
  /** When stalled: the likely causes, most likely first. */
  causes: StallCause[];
}

function windowDays(S: KayaState, today: string, w: Window): number {
  const sinceStart = S.profile.startDate ? Math.max(0, daysBetween(S.profile.startDate, today)) : null;
  if (w === 0) return sinceStart ?? 0;
  return sinceStart === null ? w : Math.min(w, sinceStart);
}

function spanStats(S: KayaState, today: string, first: number, n: number, now: Date) {
  const days: DayLedger[] = [];
  for (let i = first + n - 1; i >= first; i--) days.push(dayLedger(S, addDays(today, -i), now));
  const paired = days.filter(averagable);
  const work = days.reduce((a, d) => a + d.measuredOut, 0);
  return {
    days,
    paired,
    inAvg: mean(paired.map((d) => d.in ?? 0)),
    // A window with days but no measured Work is a real 0, not unknown — only an empty window
    // (no days at all) has nothing to say.
    workAvg: days.length ? work / days.length : null,
  };
}

/**
 * In, Out and Work over a window, set against the scale (research E1–E3, G3).
 *
 * The deficit and the split use logged days only, so an unlogged day is never a 2,000 kcal
 * deficit; the expected change then assumes the unlogged days were like the logged ones, which is
 * the assumption the coverage line names.
 */
export function energyView(
  S: KayaState,
  today: string,
  w: Window = 28,
  cycleAffected: CycleAffected = never
): EnergyView {
  const now = parseDay(today);
  const n = windowDays(S, today, w);
  const cur = spanStats(S, today, 1, n, now);
  const { days, paired } = cur;
  const outs = days.filter((d) => d.out !== null).map((d) => d.out ?? 0);
  const deficit = paired.length ? Math.round(mean(paired.map((d) => (d.in ?? 0) - (d.out ?? 0))) ?? 0) : null;

  const { raw, trend } = cleanWeights(S, today, cycleAffected);
  const start = addDays(today, -n);
  const inWin = raw.filter((p) => p.date >= start);
  const trendIn = trend.filter((p) => p.date >= start);
  const trendSpan = trendIn.length >= 2 ? daysBetween(trendIn[0].date, trendIn[trendIn.length - 1].date) : 0;
  const rate = n ? trendRate(trend, n, today) : null;
  const actualKg = rate !== null && trendSpan >= ENERGY_MIN_TREND_SPAN ? (rate * n) / 7 : null;
  const expectedKg = deficit === null ? null : (deficit * n) / KCAL_PER_KG_FAT;
  const agreeReady = paired.length >= ENERGY_MIN_LOGGED && actualKg !== null && expectedKg !== null;
  const affectedWeighIns = inWin.filter((p) => cycleAffected(p.date)).length;

  const logged = days.filter((d) => d.in !== null && d.mark === null).length;
  const inAvg = cur.inAvg === null ? null : Math.round(cur.inAvg);
  let impliedBlock: EnergyView["impliedBlock"] = null;
  if (paired.length < IMPLIED_MIN_LOGGED) impliedBlock = "logged";
  else if (rate === null || trendSpan < IMPLIED_MIN_TREND_SPAN) impliedBlock = "trend";
  else if (logged / n < IMPLIED_MIN_COVERAGE) impliedBlock = "coverage";
  else if (inWin.length && affectedWeighIns / inWin.length > IMPLIED_MAX_AFFECTED) impliedBlock = "cycle";
  const impliedOut =
    impliedBlock === null && inAvg !== null && rate !== null
      ? Math.round(inAvg - (rate / 7) * KCAL_PER_KG_FAT)
      : null;

  let split: EnergyView["split"] = null;
  if (agreeReady && deficit !== null && deficit < 0) {
    const work = Math.round(mean(paired.map((d) => d.measuredOut)) ?? 0);
    split = { food: -deficit - work, work };
  }

  // The stall looks at the last three weeks whatever the window: "flat for three weeks" is the
  // definition, and a 90-day window would bury a fresh stall under the loss before it.
  let stalled: boolean | null = null;
  let change21: number | null = null;
  const causes: StallCause[] = [];
  if (raw.length >= STALL_MIN_WEIGH_INS && trend.length && trend[0].date <= addDays(today, -STALL_DAYS)) {
    const a = trendAt(trend, addDays(today, -STALL_DAYS));
    const b = trendAt(trend, today);
    if (a !== null && b !== null) {
      change21 = b - a;
      stalled = Math.abs(change21) <= STALL_KG;
    }
  }
  if (stalled) {
    // Nothing cycle-related is said with tracking off, whatever predicate the page passed.
    const recent = cycleOn(S)
      ? raw.filter((p) => p.date > addDays(today, -STALL_DAYS) && cycleAffected(p.date)).length
      : 0;
    if (recent)
      causes.push({
        kind: "cycle",
        text: `${plural(recent, "recent weigh-in")} fell in the days around a period — likely water`,
      });
    if (n && logged / n < IMPLIED_MIN_COVERAGE)
      causes.push({ kind: "coverage", text: `food logged on only ${logged} of ${n} days` });
    if (w !== 0 && n) {
      const prev = spanStats(S, today, n + 1, n, now);
      if (
        cur.inAvg !== null &&
        prev.inAvg !== null &&
        paired.length >= ENERGY_MIN_LOGGED &&
        prev.paired.length >= ENERGY_MIN_LOGGED &&
        cur.inAvg - prev.inAvg >= STALL_IN_UP_KCAL
      )
        causes.push({
          kind: "eating",
          text: `In up ~${kcalWord(cur.inAvg - prev.inAvg)} kcal a day on the ${n} days before`,
        });
      if (prev.workAvg !== null && prev.workAvg - (cur.workAvg ?? 0) >= STALL_WORK_DOWN_KCAL)
        causes.push({
          kind: "moving",
          text: `Work down ~${kcalWord(prev.workAvg - (cur.workAvg ?? 0))} kcal a day on the ${n} days before`,
        });
    }
  }

  return {
    window: w,
    days,
    of: n,
    logged,
    paired: paired.length,
    inAvg,
    outAvg: outs.length ? Math.round(mean(outs) ?? 0) : null,
    workAvg: cur.workAvg === null ? null : Math.round(cur.workAvg),
    deficit,
    expectedKg,
    actualKg,
    gapKg: agreeReady && expectedKg !== null && actualKg !== null ? Math.abs(expectedKg - actualKg) : null,
    impliedOut,
    impliedBlock,
    split,
    weighIns: inWin.length,
    affectedWeighIns,
    trendSpan,
    stalled,
    change21,
    causes,
  };
}

/** Energy's sentences: the log against the scale, the implied Out, the split, the stall, and
 *  how much of the window the food figures rest on. */
export function energyInsights(
  S: KayaState,
  today: string,
  w: Window = 28,
  cycleAffected: CycleAffected = never
): Insight[] {
  const v = energyView(S, today, w, cycleAffected);
  const out: Insight[] = [];
  const win = w === 0 ? "since you started" : plural(v.of, "day");
  const inWin = w === 0 ? "since you started" : `in ${plural(v.of, "day")}`;
  const loggedBasis = basedOn(plural(v.paired, "logged day"), win);
  const food = foodOn(S);

  // The first-week wording counts today, as "2 of 5 days logged so far" does on the canvas.
  const clipped = w === 0 || v.of < w;
  const todayIn = dayIn(S, today) !== null ? 1 : 0;
  const soFar = clipped
    ? `${v.logged + todayIn} of ${plural(v.of + 1, "day")} logged so far.`
    : `${v.logged} of ${plural(v.of, "day")} logged.`;
  const agreeWait = (): Insight | null => {
    if (!food) return null;
    if (v.paired < ENERGY_MIN_LOGGED)
      return {
        key: "energy.agree",
        text: `${soFar} Log food on 7 days to compare the log with the scale.`,
        basedOn: plural(v.paired, "logged day"),
        wait: `after ${plural(ENERGY_MIN_LOGGED - v.paired, "more logged day")}`,
      };
    return {
      key: "energy.agree",
      text: "The log is compared with the scale once the trend has 14 days of weigh-ins.",
      basedOn: basedOn(plural(v.weighIns, "weigh-in"), win),
      wait: v.trendSpan < ENERGY_MIN_TREND_SPAN ? moreDays(ENERGY_MIN_TREND_SPAN - v.trendSpan) : "after the next weigh-in",
    };
  };

  // E1
  if (v.gapKg !== null && v.expectedKg !== null && v.actualKg !== null) {
    const e = signed(v.expectedKg, 1);
    const a = signed(v.actualKg, 1);
    const text =
      v.gapKg <= AGREE_KG
        ? `The log predicted ${e} kg and the scale shows ${a} kg: they agree within ${Math.max(
            0.1,
            Math.ceil(v.gapKg * 10) / 10
          ).toFixed(1)} kg.`
        : v.actualKg > v.expectedKg
          ? `The log predicted ${e} kg but the scale shows ${a} kg. Either Out runs high or some food goes unlogged — oil and snacks are the usual gap.`
          : `The log predicted ${e} kg but the scale shows ${a} kg. Early water loss, or an Out estimate that runs low, would explain it.`;
    out.push({ key: "energy.agree", text, basedOn: loggedBasis, tag: "est" });
  } else {
    const iw = agreeWait();
    if (iw) out.push(iw);
  }

  // E2
  if (food) {
    const link = { to: "/progress/food", label: "See Food" };
    if (v.impliedOut !== null && v.outAvg !== null) {
      const d = v.impliedOut - v.outAvg;
      const I = kcalWord(v.impliedOut);
      const O = kcalWord(v.outAvg);
      const text =
        Math.abs(d) <= IMPLIED_SAME_KCAL
          ? "No hidden gap: the scale implies the same Out that Kaya estimates. When they drift apart, the log is usually what moved."
          : d < 0
            ? `The scale implies ~${I} kcal a day; Kaya estimates ~${O}. Either your burn is ~${kcalWord(d)} lower or the log misses ~${kcalWord(d)} a day — oil and snacks are the usual gap.`
            : `The scale implies ~${I} kcal a day, above Kaya's ~${O}. Early water loss, or an Out estimate that runs low, would explain it.`;
      out.push({
        key: "energy.implied",
        text,
        basedOn: basedOn(`${plural(v.paired, "logged day")} and the trend`, win),
        tag: "est",
        link,
      });
    } else {
      const b = v.impliedBlock;
      const wait: Insight =
        b === "logged"
          ? {
              key: "energy.implied",
              text: "Out implied by the scale appears after 14 logged days.",
              basedOn: loggedBasis,
              wait: `after ${plural(IMPLIED_MIN_LOGGED - v.paired, "more logged day")}`,
            }
          : b === "coverage"
            ? {
                key: "energy.implied",
                text: `Out implied by the scale needs food logged on 6 days in 10; this window has ${Math.round(
                  (v.logged / Math.max(1, v.of)) * 10
                )} in 10.`,
                basedOn: loggedBasis,
                wait: "after more logged days",
              }
            : b === "cycle"
              ? {
                  key: "energy.implied",
                  text: `Out implied by the scale is held back: ${v.affectedWeighIns} of ${plural(
                    v.weighIns,
                    "weigh-in"
                  )} in this window fell in the days around a period.`,
                  basedOn: basedOn(plural(v.weighIns, "weigh-in"), win),
                  tag: "cycle",
                  wait: "after the next weigh-ins",
                }
              : {
                  key: "energy.implied",
                  text: "Out implied by the scale appears once the trend has 21 days of weigh-ins.",
                  basedOn: basedOn(plural(v.weighIns, "weigh-in"), win),
                  wait: moreDays(IMPLIED_MIN_TREND_SPAN - v.trendSpan),
                };
      out.push(wait);
    }
  }

  // E3
  if (food) {
    const link = { to: "/progress/training", label: "See Training" };
    if (v.split && v.deficit !== null) {
      const D = -v.deficit;
      const share = v.split.food / D;
      // Eating above the burn before Work leaves a food share below zero; "Work: ~450 of ~370"
      // would read as a sum that does not add up, so that case is named for what it is.
      const text =
        v.split.food < 0
          ? `All of the deficit is Work: without it In would run ~${kcalWord(-v.split.food)} kcal a day above Out, and Work's ~${kcalWord(v.split.work)} takes it back under.`
          : share >= 0.35 && share <= 0.65
          ? "About half the deficit is the food budget and half is Work — a missed session week shows up on the scale a week later."
          : share > 0.65
            ? `Most of the deficit is the food budget: ~${kcalWord(v.split.food)} of ~${kcalWord(D)} kcal a day. Work adds ~${kcalWord(v.split.work)}.`
            : `Most of the deficit is Work: ~${kcalWord(v.split.work)} of ~${kcalWord(D)} kcal a day. Food is close to your burn, so a missed session week shows up on the scale.`;
      out.push({ key: "energy.split", text, basedOn: loggedBasis, tag: "est", link });
    } else if (v.gapKg !== null && v.deficit !== null) {
      out.push({
        key: "energy.split",
        text: `No deficit on logged days: In ran ~${kcalWord(v.deficit)} kcal a day above Out.`,
        basedOn: loggedBasis,
        tag: "est",
        link,
      });
    } else {
      const iw = agreeWait();
      if (iw)
        out.push({
          ...iw,
          key: "energy.split",
          text: "Where the deficit comes from appears once the log can be compared with the scale.",
        });
    }
  }

  // G3
  if (v.stalled === null) {
    const all = weightSeries(S).filter((p) => p.date <= today);
    const span = all.length ? daysBetween(all[0].date, today) : 0;
    out.push({
      key: "energy.stall",
      text: "Stalls are spotted once the trend has 21 days of weigh-ins.",
      basedOn: all.length ? plural(all.length, "weigh-in") : "no weigh-ins yet",
      wait:
        all.length < STALL_MIN_WEIGH_INS
          ? `after ${plural(STALL_MIN_WEIGH_INS - all.length, "more weigh-in")}`
          : moreDays(STALL_DAYS - span),
    });
  } else if (!v.stalled) {
    const change = v.actualKg ?? v.change21 ?? 0;
    const over = v.actualKg !== null ? inWin : "in 21 days";
    out.push({
      key: "energy.stall",
      text: `Not stalled: the trend ${change < 0 ? "fell" : "rose"} ${Math.abs(change).toFixed(1)} kg ${over}. When it is flat for three weeks Kaya lists the likely causes in order — ${cycleOn(S) ? "cycle water, " : ""}missing log days, eating more, moving less.`,
      basedOn: basedOn("the trend", win),
    });
  } else {
    const moved = `Flat for three weeks: the trend moved ${signed(v.change21 ?? 0, 1)} kg in 21 days.`;
    out.push({
      key: "energy.stall",
      text: v.causes.length
        ? `${moved} Likely causes, most likely first: ${v.causes.map((c) => c.text).join("; ")}.`
        : `${moved} None of the usual causes shows — at a lower weight the burn is lower, so the deficit may have closed.`,
      basedOn: basedOn("the trend", loggedBasis),
      ...(v.causes[0]?.kind === "cycle" ? { tag: "cycle" as const } : {}),
    });
  }

  // F1
  if (!food) {
    out.push({
      key: "energy.coverage",
      text: "Food logging is off, so In is not known; Out and Work are still estimated.",
      basedOn: basedOn(win),
    });
  } else if (!v.of) {
    out.push({
      key: "energy.coverage",
      text: "Coverage appears after your first full day.",
      basedOn: "today only",
      wait: "after 1 more day",
    });
  } else if (!v.logged) {
    out.push({
      key: "energy.coverage",
      text: `No food logged ${w === 0 ? "since you started" : `in the last ${plural(v.of, "day")}`}. In against Out appears once a day is logged.`,
      basedOn: basedOn(win),
      wait: "after 1 logged day",
    });
  } else {
    const pct = Math.round((v.logged / v.of) * 100);
    const unlogged = v.of - v.logged;
    out.push({
      key: "energy.coverage",
      text:
        unlogged === 0
          ? `${v.logged} of ${plural(v.of, "day")} logged. Every food figure on this page uses all of them.`
          : `${v.logged} of ${plural(v.of, "day")} logged. Every food figure on this page is from those days; the ${plural(
              unlogged,
              "unlogged day"
            )} ${unlogged === 1 ? "is" : "are"} not counted as zero.`,
      basedOn: basedOn(win, `${pct} % of days`),
    });
  }
  return out;
}
